import React, { useState, useMemo } from 'react';
import { useSessionLaunch } from '../lib/useSessionLaunch';
import { ProjectProvider } from '../context/ProjectContext';
import ProjectsDashboard from '../components/projects/ProjectsDashboard';
import ProjectList from '../components/projects/ProjectList';
import ProjectDetail from '../components/projects/ProjectDetail';
import ProjectAnalytics from '../components/projects/ProjectAnalytics';
import ProjectSettings from '../components/projects/ProjectSettings';
import { useProjects } from '../context/ProjectContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { LayoutDashboard, List, BarChart3, Settings, Plus, X, Tag, Loader2 } from 'lucide-react';
import usePageMeta from '../hooks/usePageMeta';

const ProjectsAppContent = () => {
  usePageMeta('Harmony 2.0 Projects', '/favicon.ico');

  const [activeTab, setActiveTab] = useState('dashboard');
  const [viewingProjectId, setViewingProjectId] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const handleViewProject = (id) => {
    setViewingProjectId(id);
  };

  const handleBackFromDetail = () => {
    setViewingProjectId(null);
  };

  if (viewingProjectId) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <ProjectDetail
          projectId={viewingProjectId}
          onBack={handleBackFromDetail}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="border-b bg-white">
        <div className="px-6 py-4">
          <h1 className="text-xl font-bold text-slate-800">Harmony 2.0 Projects</h1>
          <p className="text-sm text-slate-500">Project Proposal Tracking System</p>
        </div>
      </div>
      <div className="p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="dashboard" className="gap-1.5">
              <LayoutDashboard className="w-4 h-4" />Dashboard
            </TabsTrigger>
            <TabsTrigger value="projects" className="gap-1.5">
              <List className="w-4 h-4" />Projects
            </TabsTrigger>
            <TabsTrigger value="analytics" className="gap-1.5">
              <BarChart3 className="w-4 h-4" />Analytics
            </TabsTrigger>
            <TabsTrigger value="settings" className="gap-1.5">
              <Settings className="w-4 h-4" />Settings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard">
            <ProjectsDashboard
              onNewProject={() => setShowCreateDialog(true)}
              onViewProject={handleViewProject}
            />
          </TabsContent>

          <TabsContent value="projects">
            <ProjectList
              onNewProject={() => setShowCreateDialog(true)}
              onViewProject={handleViewProject}
            />
          </TabsContent>

          <TabsContent value="analytics">
            <ProjectAnalytics />
          </TabsContent>

          <TabsContent value="settings">
            <ProjectSettings />
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Project Dialog */}
      <CreateProjectDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onCreated={(id) => { setShowCreateDialog(false); handleViewProject(id); }}
      />
    </div>
  );
};

const CreateProjectDialog = ({ open, onOpenChange, onCreated }) => {
  const { createProject, getSettings, getTags, getUsersLookup } = useProjects();
  const [form, setForm] = useState({
    title: '', platform: 'Upwork', project_url: '', budget_rate: '',
    client_name: '', client_location: '', cover_letter: '',
    proposal_date: new Date().toISOString().slice(0, 10),
    expected_duration: '', tags: [], assigned_users: []
  });
  const [saving, setSaving] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);
  const [allTags, setAllTags] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loaded, setLoaded] = useState(false);

  React.useEffect(() => {
    if (open && !loaded) {
      const load = async () => {
        const [settings, tags, users] = await Promise.all([
          getSettings(), getTags(), getUsersLookup()
        ]);
        setPlatforms(settings?.platforms || ['Upwork', 'Freelancer', 'Fiverr', 'LinkedIn', 'Toptal', 'Direct Client', 'Other']);
        setAllTags(tags || []);
        setAllUsers(users || []);
        setLoaded(true);
      };
      load();
    }
    if (!open) {
      setForm({
        title: '', platform: 'Upwork', project_url: '', budget_rate: '',
        client_name: '', client_location: '', cover_letter: '',
        proposal_date: new Date().toISOString().slice(0, 10),
        expected_duration: '', tags: [], assigned_users: []
      });
      setTagInput('');
      setLoaded(false);
    }
  }, [open]);

  const addTag = (tagValue) => {
    const tag = (tagValue || tagInput).trim();
    if (tag && !form.tags.includes(tag)) {
      setForm(prev => ({ ...prev, tags: [...prev.tags, tag] }));
    }
    setTagInput('');
    setShowTagSuggestions(false);
  };

  const filteredTagSuggestions = tagInput.trim().length > 0
    ? allTags.filter(t => t.name.toLowerCase().includes(tagInput.toLowerCase()) && !form.tags.includes(t.name)).map(t => t.name)
    : [];

  const toggleUser = (userId) => {
    setForm(prev => ({
      ...prev,
      assigned_users: prev.assigned_users.includes(userId)
        ? prev.assigned_users.filter(u => u !== userId)
        : [...prev.assigned_users, userId]
    }));
  };

  const handleCreate = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (!form.proposal_date) { toast.error('Proposal date is required'); return; }
    setSaving(true);
    const result = await createProject(form);
    setSaving(false);
    if (result.success) {
      toast.success('Project created');
      onCreated(result.data.id);
    } else {
      toast.error(result.error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="create-project-dialog">
        <DialogHeader>
          <DialogTitle>New Project Proposal</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-2">
              <Label>Project Title *</Label>
              <Input value={form.title} onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))} placeholder="e.g. E-commerce Platform Redesign" data-testid="create-title" />
            </div>
            <div className="space-y-2">
              <Label>Platform</Label>
              <Select value={form.platform} onValueChange={(v) => setForm(prev => ({ ...prev, platform: v }))}>
                <SelectTrigger data-testid="create-platform"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {platforms.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Proposal Date *</Label>
              <Input type="date" value={form.proposal_date} onChange={(e) => setForm(prev => ({ ...prev, proposal_date: e.target.value }))} data-testid="create-date" />
            </div>
            <div className="space-y-2">
              <Label>Project URL</Label>
              <Input value={form.project_url} onChange={(e) => setForm(prev => ({ ...prev, project_url: e.target.value }))} placeholder="https://..." />
            </div>
            <div className="space-y-2">
              <Label>Budget / Rate</Label>
              <Input value={form.budget_rate} onChange={(e) => setForm(prev => ({ ...prev, budget_rate: e.target.value }))} placeholder="e.g. $50/hr or $5000 fixed" />
            </div>
            <div className="space-y-2">
              <Label>Client Name</Label>
              <Input value={form.client_name} onChange={(e) => setForm(prev => ({ ...prev, client_name: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Client Location</Label>
              <Input value={form.client_location} onChange={(e) => setForm(prev => ({ ...prev, client_location: e.target.value }))} />
            </div>
            <div className="col-span-2 space-y-2">
              <Label>Expected Duration</Label>
              <Input value={form.expected_duration} onChange={(e) => setForm(prev => ({ ...prev, expected_duration: e.target.value }))} placeholder="e.g. 3 months" />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Cover Letter</Label>
            <Textarea value={form.cover_letter} onChange={(e) => setForm(prev => ({ ...prev, cover_letter: e.target.value }))} rows={4} placeholder="Your proposal cover letter..." />
          </div>

          {/* Tags */}
          <div className="space-y-2">
            <Label>Tags</Label>
            <div className="relative">
              <div className="flex gap-2">
                <Input
                  value={tagInput}
                  onChange={(e) => { setTagInput(e.target.value); setShowTagSuggestions(true); }}
                  placeholder="Add a tag..."
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  onFocus={() => setShowTagSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowTagSuggestions(false), 200)}
                  data-testid="create-tag-input"
                />
                <Button type="button" variant="outline" onClick={() => addTag()}><Plus className="w-4 h-4" /></Button>
              </div>
              {showTagSuggestions && filteredTagSuggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-32 overflow-y-auto">
                  {filteredTagSuggestions.map((tag, idx) => (
                    <div key={idx} className="px-3 py-1.5 text-sm cursor-pointer hover:bg-blue-50" onMouseDown={(e) => { e.preventDefault(); addTag(tag); }}>
                      {tag}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {form.tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {form.tags.map((tag, idx) => (
                  <Badge key={idx} variant="outline" className="gap-1 bg-blue-50 text-blue-700 border-blue-200">
                    <Tag className="w-3 h-3" />{tag}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => setForm(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }))} />
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Team Members */}
          <div className="space-y-2">
            <Label>Assign Team Members</Label>
            <div className="flex flex-wrap gap-2 p-3 border rounded-md bg-slate-50">
              {allUsers.length === 0 ? (
                <p className="text-sm text-slate-400">Loading users...</p>
              ) : allUsers.map(user => (
                <Badge
                  key={user.id}
                  variant={form.assigned_users.includes(user.id) ? "default" : "outline"}
                  className={`cursor-pointer ${form.assigned_users.includes(user.id) ? 'bg-blue-600' : ''}`}
                  onClick={() => toggleUser(user.id)}
                >
                  {user.full_name || user.username}
                </Badge>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleCreate} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="create-submit-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
            Create Project
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ProjectsApp = () => {
  // Signed in from the visitor's own session (see useSessionLaunch).
  const { token } = useSessionLaunch();

  return (
    <ProjectProvider token={token}>
      <ProjectsAppContent />
    </ProjectProvider>
  );
};

export default ProjectsApp;
